// src/scenes/companies/CompanyPolicies.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { tokens } from "../../theme";
import { toast } from "react-toastify";
import Header from "../../components/Header";

const initialForm = { title: "", description: "" };

const CompanyPolicies = () => {
  const { id: companyId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [deleteId, setDeleteId] = useState(null);

  /** ------------ FETCH POLICIES ------------ */
  const fetchPolicies = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(`/companies/${companyId}/policies`)
      .then((res) => setPolicies(res.data || []))
      .catch(() => toast.error("Failed to load policies"))
      .finally(() => setLoading(false));
  },[companyId]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  /** ------------ ADD / EDIT ------------ */
  const openAddEdit = (row = null) => {
    setEditing(row);
    setFormData(
      row ? { title: row.title, description: row.description } : initialForm,
    );
    setAddEditOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleConfirmSave = () => {
    if (!formData.title.trim() || !formData.description.trim())
      return toast.warn("Title and description are required");
    setConfirmOpen(true);
  };

  const submitAddEdit = () => {
    setLoading(true);
    const request = editing
      ? axiosInstance.put(
          `/companies/${companyId}/policies/${editing.id}`,
          formData,
        )
      : axiosInstance.post(`/companies/${companyId}/policies`, formData);

    request
      .then(() => {
        toast.success(`Policy ${editing ? "updated" : "added"} successfully`);
        fetchPolicies();
        setAddEditOpen(false);
      })
      .catch(() =>
        toast.error(`Failed to ${editing ? "update" : "add"} policy`),
      )
      .finally(() => {
        setLoading(false);
        setConfirmOpen(false);
      });
  };

  /** ------------ DELETE ------------ */
  const openDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const submitDelete = () => {
    setLoading(true);
    axiosInstance
      .delete(`/companies/${companyId}/policies/${deleteId}`)
      .then(() => {
        toast.success("Policy deleted");
        fetchPolicies();
      })
      .catch(() => toast.error("Failed to delete policy"))
      .finally(() => {
        setLoading(false);
        setDeleteOpen(false);
        setDeleteId(null);
      });
  };

  /** ------------ DATA GRID ------------ */
  const columns = useMemo(
    () => [
      { field: "title", headerName: "Title", flex: 1, minWidth: 150 },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 300,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton
              color="primary"
              disabled={loading}
              onClick={() => openAddEdit(row)}
            >
              <Edit />
            </IconButton>
            <IconButton
              color="error"
              disabled={loading}
              onClick={() => openDelete(row.id)}
            >
              <Delete />
            </IconButton>
          </Box>
        ),
      },
    ],
    [loading],
  );

  return (
    <Box m="20px">
      <Header title="COMPANY POLICIES" subtitle={`Company ID: ${companyId}`} />

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}
          variant="contained"
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: colors.grey[900],
          }}
          onClick={() => openAddEdit()}
          disabled={loading}
        >
          Add Policy
        </Button>
      </Box>

      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={policies}
          columns={columns}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 20]}
          loading={loading}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={addEditOpen}
        onClose={() => setAddEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editing ? "Edit Policy" : "Add Policy"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, mt: 1 }}>
          <TextField
            label="Title"
            name="title"
            fullWidth
            value={formData.title}
            onChange={handleFormChange}
            autoFocus
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={5}
            value={formData.description}
            onChange={handleFormChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddEditOpen(false)}
            disabled={loading}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Confirm */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Save this policy?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setConfirmOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitAddEdit}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Yes, Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete this policy?</DialogTitle>
        <DialogActions>
          <Button
            color="secondary"
            onClick={() => setDeleteOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitDelete}
            disabled={loading}
            sx={{ color: colors.redAccent[500] }}
          >
            {loading ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyPolicies;
